import type { ChatTurn, SearchMatch } from './chat-turns.js';

import React, { useState } from 'react';

import { cn } from '@elara/client-core/lib/utils';

import { useChatsState, useMessageViewState } from '#states/hooks.js';
import { Badge, Button, Card, CardContent, Input, MaterialIcon, ScrollArea, TabsContent } from '#ui/index.js';

import { buildChatTurns, messageText, searchTurns } from './chat-turns.js';

const MIN_QUERY_LENGTH = 2;
const MAX_SHOWN_MATCHES = 10;

const TURN_BUTTON_CLASSES = 'h-7 w-8 px-0 text-xs shrink-0';
// Match the widget cards in the Live tab, so the inspector tabs look alike.
const WIDGET_CARD_CLASSES = 'border-gray-500/50 bg-gray-500/5';

/**
 * Debug Inspector tab for navigating long conversations: turn stats, a turn stepper and text
 * search over the current chat's questions and responses.
 */
export function ChatTurnsTab() {
  const chatsState = useChatsState();
  const chatId = chatsState.current?.id;

  return (
    <TabsContent value="turns" className="flex-1 overflow-hidden px-3">
      {/* Keyed by chat so the picked turn and search reset when the chat changes. */}
      {chatId ? <ChatTurnsNavigator key={chatId} /> : <EmptyNotice text="Open a chat to navigate its turns" />}
    </TabsContent>
  );
}

function ChatTurnsNavigator() {
  const chatsState = useChatsState();
  const messageViewState = useMessageViewState();
  const [selectedTurn, setSelectedTurn] = useState<number>();

  const turns = buildChatTurns(chatsState.messagesList);
  if (turns.length === 0) return <EmptyNotice text="No turns in this chat yet" />;

  // The chat opens scrolled to the bottom, so the last turn is current until one is picked.
  const currentTurn = Math.min(selectedTurn ?? turns.length, turns.length);
  const responseCount = turns.reduce((count, turn) => count + turn.responses.length, 0);

  // Jumps are instant: a smooth scroll from the bottom grows the list as rows mount, and the
  // chat's follow-output pulls it straight back down.
  const goToTurn = (turnNumber: number) => {
    const turn = turns[turnNumber - 1];
    if (!turn) return;

    setSelectedTurn(turnNumber);
    messageViewState.scrollToMessage(turn.question.id, 'auto', 'start');
  };

  const goToMatch = (match: SearchMatch) => {
    setSelectedTurn(match.turnNumber);
    messageViewState.scrollToMessage(match.message.id, 'auto');
  };

  return (
    <ScrollArea className="h-full">
      <div className="flex flex-col gap-3 pr-3">
        <TurnsOverviewCard turnCount={turns.length} responseCount={responseCount} />
        <GoToTurnCard turns={turns} currentTurn={currentTurn} onGoToTurn={goToTurn} />
        <TurnSearch turns={turns} onGoToMatch={goToMatch} />
      </div>
    </ScrollArea>
  );
}

// Styled like the Working Memory card in the Live tab.
function TurnsOverviewCard({ turnCount, responseCount }: { turnCount: number; responseCount: number }) {
  return (
    <Card className="border-blue-500/50 bg-blue-500/5">
      <CardContent className="py-3 px-3">
        <div className="flex items-center gap-2 mb-2">
          <MaterialIcon icon="forum" className="text-blue-600" />
          <span className="text-sm font-medium">Turns</span>
          <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-600">
            {turnCount}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">Each turn is one question and the replies that follow it.</p>
        <p className="text-xs text-muted-foreground mt-1">
          {turnCount} questions · {responseCount} responses
        </p>
      </CardContent>
    </Card>
  );
}

function GoToTurnCard({
  turns,
  currentTurn,
  onGoToTurn,
}: {
  turns: ChatTurn[];
  currentTurn: number;
  onGoToTurn: (turnNumber: number) => void;
}) {
  const currentTurnQuestion = turns[currentTurn - 1]?.question;
  const currentQuestion = currentTurnQuestion ? messageText(currentTurnQuestion) : '';

  return (
    <Card className={WIDGET_CARD_CLASSES}>
      <div className="p-3 flex items-start gap-3">
        <MaterialIcon icon="format_list_numbered" className="mt-0.5 text-muted-foreground" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-medium text-sm">Go to turn</h4>
            <div className="ml-auto">
              <TurnStepper turns={turns} currentTurn={currentTurn} onGoToTurn={onGoToTurn} />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2 truncate">
            <span className="font-medium text-foreground">Question</span>
            {` · ${currentQuestion || 'No text'}`}
          </p>
        </div>
      </div>
    </Card>
  );
}

function TurnStepper({
  turns,
  currentTurn,
  onGoToTurn,
}: {
  turns: ChatTurn[];
  currentTurn: number;
  onGoToTurn: (turnNumber: number) => void;
}) {
  // Holds what is typed until Enter, so the field shows the current turn the rest of the time.
  const [typedTurn, setTypedTurn] = useState<string>();

  const goToTypedTurn = () => {
    const turnNumber = Number(typedTurn);
    setTypedTurn(undefined);
    if (Number.isInteger(turnNumber)) onGoToTurn(Math.min(Math.max(turnNumber, 1), turns.length));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') goToTypedTurn();
    if (event.key === 'Escape') setTypedTurn(undefined);
  };

  return (
    <nav aria-label="Chat turns" className="flex items-center gap-1.5">
      <Button
        variant="outline"
        size="sm"
        className={TURN_BUTTON_CLASSES}
        aria-label="Previous turn"
        disabled={currentTurn === 1}
        onClick={() => onGoToTurn(currentTurn - 1)}
      >
        ‹
      </Button>
      <Input
        value={typedTurn ?? String(currentTurn)}
        onChange={(event) => setTypedTurn(event.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={(event) => event.target.select()}
        onBlur={() => setTypedTurn(undefined)}
        inputMode="numeric"
        aria-label="Turn number"
        className="h-7 w-12 px-1 text-center text-xs"
      />
      <span className="text-xs text-muted-foreground">/ {turns.length}</span>
      <Button
        variant="outline"
        size="sm"
        className={TURN_BUTTON_CLASSES}
        aria-label="Next turn"
        disabled={currentTurn === turns.length}
        onClick={() => onGoToTurn(currentTurn + 1)}
      >
        ›
      </Button>
    </nav>
  );
}

// Results show while the field has focus, so clicking elsewhere (the chat, Go to turn) hides them
// and keeps the text for when you come back.
function TurnSearch({ turns, onGoToMatch }: { turns: ChatTurn[]; onGoToMatch: (match: SearchMatch) => void }) {
  const [query, setQuery] = useState('');
  const [highlightedMatchIndex, setHighlightedMatchIndex] = useState<number>();
  const [isResultsOpen, setIsResultsOpen] = useState(false);

  const trimmedQuery = query.trim();
  const isSearching = trimmedQuery.length >= MIN_QUERY_LENGTH;
  const matches = isSearching ? searchTurns(turns, trimmedQuery) : [];
  const shownMatches = matches.slice(0, MAX_SHOWN_MATCHES);
  const areResultsVisible = isSearching && isResultsOpen;

  const goToShownMatch = (index: number) => {
    const match = shownMatches[index];
    if (!match) return;

    setHighlightedMatchIndex(index);
    onGoToMatch(match);
  };

  const changeQuery = (nextQuery: string) => {
    setQuery(nextQuery);
    setHighlightedMatchIndex(undefined);
    setIsResultsOpen(true);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const lastIndex = shownMatches.length - 1;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setIsResultsOpen(true);
        setHighlightedMatchIndex(Math.min((highlightedMatchIndex ?? -1) + 1, lastIndex));
        return;
      case 'ArrowUp':
        event.preventDefault();
        setIsResultsOpen(true);
        setHighlightedMatchIndex(Math.max((highlightedMatchIndex ?? 0) - 1, 0));
        return;
      case 'Enter':
        if (highlightedMatchIndex !== undefined) goToShownMatch(highlightedMatchIndex);
        return;
      case 'Escape':
        // Each press steps back one level: hide the results, then clear the text, then leave the field.
        if (areResultsVisible && matches.length > 0) {
          setIsResultsOpen(false);
          return;
        }

        if (query === '') event.currentTarget.blur();
        changeQuery('');
    }
  };

  return (
    <section className="flex flex-col gap-2">
      <Card className={WIDGET_CARD_CLASSES}>
        <div className="p-3 flex items-start gap-3">
          <MaterialIcon icon="search" className="mt-0.5 text-muted-foreground" />
          <div className="flex-1 min-w-0">
            {/* Fixed height, so the match count badge (taller than the title) doesn't push the card down. */}
            <div className="flex items-center gap-2 h-5">
              <h4 className="font-medium text-sm">Search</h4>
              {areResultsVisible && (
                <Badge variant="outline" className="text-xs border-green-500/50 bg-green-500/10 text-green-600">
                  {matches.length}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Find text in this chat's questions and responses. Use ↓ ↑ to pick a result, Enter to jump to it and Esc to
              close.
            </p>
            <Input
              value={query}
              onChange={(event) => changeQuery(event.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => setIsResultsOpen(true)}
              onBlur={() => setIsResultsOpen(false)}
              placeholder="Type at least 2 characters"
              className="h-9 text-xs mt-2"
            />
            {areResultsVisible && matches.length === 0 && (
              <p className="text-xs text-muted-foreground mt-1">No matches</p>
            )}
          </div>
        </div>
      </Card>
      {areResultsVisible && matches.length > 0 && (
        <SearchResults
          matches={shownMatches}
          hiddenCount={matches.length - shownMatches.length}
          highlightedIndex={highlightedMatchIndex}
          onSelect={goToShownMatch}
        />
      )}
    </section>
  );
}

function SearchResults({
  matches,
  hiddenCount,
  highlightedIndex,
  onSelect,
}: {
  matches: SearchMatch[];
  hiddenCount: number;
  highlightedIndex: number | undefined;
  onSelect: (index: number) => void;
}) {
  // Same green as a completed widget card in the Live tab.
  return (
    <ul
      className="max-h-80 overflow-y-auto rounded-md border border-green-500/50 bg-green-500/5"
      // Keeps focus in the search field, so clicking a result or the scrollbar doesn't close the list.
      onMouseDown={(event) => event.preventDefault()}
    >
      {matches.map((match, index) => (
        <li key={match.message.id} ref={index === highlightedIndex ? scrollHighlightedRowIntoView : undefined}>
          <button
            type="button"
            className={cn(
              'w-full text-left text-xs px-3 py-1.5 hover:bg-green-500/10',
              index === highlightedIndex && 'bg-green-500/10',
            )}
            onClick={() => onSelect(index)}
          >
            <span className="font-medium">
              Turn {match.turnNumber} · {match.message.isUserMessage ? 'Question' : 'Response'}
            </span>{' '}
            <span className="text-muted-foreground">{match.excerpt}</span>
          </button>
        </li>
      ))}
      {hiddenCount > 0 && (
        <li className="px-3 py-1.5 text-xs text-muted-foreground">
          … {hiddenCount} more, refine the search to narrow down
        </li>
      )}
    </ul>
  );
}

function scrollHighlightedRowIntoView(row: HTMLLIElement | null) {
  row?.scrollIntoView({ block: 'nearest' });
}

function EmptyNotice({ text }: { text: string }) {
  return <div className="text-center py-8 text-muted-foreground">{text}</div>;
}
